import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** Container padrão: 1200px, gutter 16/24px. */
export function Container({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`max-w-[1200px] mx-auto px-4 md:px-6 ${className}`}>{children}</div>;
}

/** Título de seção estilo Apple: grande, curto, centralizado. */
export function SectionTitle({
  eyebrow,
  title,
  sub,
  align = 'center',
  dark = false,
}: {
  eyebrow?: string;
  title: ReactNode;
  sub?: ReactNode;
  align?: 'center' | 'left';
  dark?: boolean;
}) {
  const a = align === 'center' ? 'text-center mx-auto' : 'text-left';
  return (
    <div className={`max-w-3xl ${a}`}>
      {eyebrow && (
        <p className={`text-[14px] md:text-[17px] font-semibold tracking-[-0.01em] ${dark ? 'text-[#F5A35C]' : 'text-[#C4500A]'}`}>
          {eyebrow}
        </p>
      )}
      <h2
        className={`mt-2 text-[34px] sm:text-[44px] md:text-[56px] leading-[1.06] tracking-[-0.04em] font-semibold ${
          dark ? 'text-white' : 'text-[#1D1D1F]'
        }`}
      >
        {title}
      </h2>
      {sub && (
        <p className={`mt-4 md:mt-5 text-[17px] md:text-[21px] leading-[1.4] tracking-[-0.015em] ${dark ? 'text-white/60' : 'text-[#6E6E73]'}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

/** Botão pílula laranja (ação principal). */
export function PillButton({
  href,
  to,
  children,
  variant = 'primary',
}: {
  href?: string;
  to?: string;
  children: ReactNode;
  variant?: 'primary' | 'dark' | 'light';
}) {
  const styles = {
    primary: 'bg-[#E8610A] text-white hover:bg-[#C4500A]',
    dark: 'bg-[#1D1D1F] text-white hover:bg-black',
    light: 'bg-white text-[#1D1D1F] hover:bg-white/90',
  }[variant];
  const cls = `inline-flex items-center justify-center text-[15px] md:text-[17px] font-normal tracking-[-0.01em] px-6 py-3 rounded-full whitespace-nowrap ${styles}`;
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {children}
    </a>
  );
}

/** Link de texto com seta "›". */
export function ArrowLink({
  to,
  href,
  children,
  dark = false,
}: {
  to?: string;
  href?: string;
  children: ReactNode;
  dark?: boolean;
}) {
  const cls = `group inline-flex items-center gap-1 text-[15px] md:text-[17px] tracking-[-0.01em] hover:underline underline-offset-4 ${
    dark ? 'text-[#F5A35C]' : 'text-[#C4500A]'
  }`;
  const inner = (
    <>
      {children}
      <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-0.5">›</span>
    </>
  );
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {inner}
    </a>
  );
}
